import * as THREE from 'three';
import type { ViewState, ViewEntity } from '../game/viewState';
import { AnimatedPlayerPool, defaultBackNumber, pickClip, isDownAction, type PlayerRig } from './animatedPlayers';
import { unicolorKit, solidSlot, type KitPaint } from './kitRecolor';
import { buildEnvironment } from './environment';
import { selectTrajectoryDots } from './trajectoryDots';
import { celebrationAnchor, celebrationBlend, celebrationFraming } from './celebrationCam';

// Dummy-shape renderer (§13) with skinned-mesh players (D21) once the GLB pool finishes loading —
// pills are the instant + permanent fallback. render/ consumes ONLY ViewState (T-ARCH-004). Sim
// world coords (x,y horizontal, z up) map to three (x, z-up, y): three = (sim.x, sim.z + heightOffset, sim.y).

/** What the scene needs from ANY player representation — pill or skinned rig (telegraphs fan out). */
interface PlayerVisual {
  readonly object3d: THREE.Object3D;
  readonly userData: Record<string, unknown>;
  /** Feet-to-origin offset: pills are centered (+0.9), rigs are planted at the feet (0). */
  readonly groundOffset: number;
  setEmissive(hex: number): void;
  setColor(hex: number): void;
  mulColor(f: number): void;
  /** Per-frame presentation update (rigs drive their mixer; pills do nothing).
   *  `celebrationSeed` rerolls the celebration clip per goal (D108); pills ignore it. */
  tick(e: ViewEntity, dtSec: number, celebrationSeed?: number): void;
}

class CapsuleVisual implements PlayerVisual {
  readonly userData: Record<string, unknown>;
  readonly groundOffset = CAPSULE_H_OFFSET;
  constructor(private readonly mesh: THREE.Mesh) {
    this.userData = mesh.userData;
  }
  get object3d(): THREE.Object3D {
    return this.mesh;
  }
  private mat(): THREE.MeshStandardMaterial {
    return this.mesh.material as THREE.MeshStandardMaterial;
  }
  setEmissive(hex: number): void {
    this.mat().emissive.setHex(hex);
  }
  setColor(hex: number): void {
    this.mat().color.setHex(hex);
  }
  mulColor(f: number): void {
    this.mat().color.multiplyScalar(f);
  }
  tick(): void {}
}

class RigVisual implements PlayerVisual {
  readonly userData: Record<string, unknown> = {};
  readonly groundOffset = 0; // the mesh is planted at its feet
  constructor(readonly rig: PlayerRig, baseColor: number) {
    this.userData['baseColor'] = baseColor;
  }
  get object3d(): THREE.Object3D {
    return this.rig.object3d;
  }
  setEmissive(hex: number): void {
    for (const m of this.rig.materials) m.emissive.setHex(hex);
  }
  setColor(hex: number): void {
    for (const m of this.rig.materials) m.color.setHex(hex);
  }
  mulColor(f: number): void {
    for (const m of this.rig.materials) m.color.multiplyScalar(f);
  }
  tick(e: ViewEntity, dtSec: number, celebrationSeed = 0): void {
    // `variant` (entity id) fixes this player's IDLE variant for the match; `celebrationSeed` (the
    // running score) only shifts the celebration, so a goal does not restyle everyone's idle. Both
    // come from data every client already has, so everyone watching sees the same thing.
    this.rig.setClip(pickClip({ action: e.action, moveSpeed: e.moveSpeed, variant: e.id, celebrationSeed }), e.moveSpeed);
    this.rig.setDown(isDownAction(e.action)); // D55: theatrical fall/tumble on floor states
    this.rig.update(dtSec);
  }
}

export interface SceneConfig {
  pitchLength: number;
  pitchWidth: number;
  goalWidth: number;
  barH: number;
  penaltyBoxDepth: number;
  penaltyBoxHalfWidth: number;
  goalAreaDepth: number;
  teamColors: [number, number];
  /** D66: optional per-team kit override for full recolor control (two-colour / unicolor / custom).
   *  Unset → outfielders use the default coloured-stripes-on-white kit built from `teamColors`. */
  teamKits?: [KitPaint, KitPaint];
  /** FAFI Touch (plan §6): DPR ceiling — phones cap at 1.5 so mid-tier GPUs hold the frame rate. */
  maxPixelRatio?: number;
}

const POST_R = 0.08;

const CAPSULE_H_OFFSET = 0.9;
const PILL_R = 0.22; // D18: visual radius ∝ ball (SIM capsule stays 0.35 — collision unchanged)
const GK_NEON: [number, number] = [0x00ffff, 0xff10f0]; // D69: keeper strips — cyan (blue team) / magenta (red team)
const GK_FLAT_V = 0.92; // D69: bright SOLID neon keeper (flat value; scene lighting supplies the 3-D form)
/** D69: black-and-white-striped referee — teal ink → flat true-black, white ink → flat bright-white. */
const REF_KIT: KitPaint = { teal: solidSlot(0x000000, 0.03), white: solidSlot(0xffffff, 0.95) };
/** D69: full SOLID neon keeper — cyan (blue) / magenta (red), distinct from both outfield colours + each other. */
function gkKit(team: ViewEntity['team']): KitPaint {
  return unicolorKit(GK_NEON[team], GK_FLAT_V);
}
/** D38: permanent keeper glow — emissive so the GK reads at a glance even in shadow/at distance.
 *  (Applied every frame in telegraph(); the old spawn-time tint was overwritten there — the "wrong
 *  player looked highlighted" bug: only the CONTROLLED entity kept an emissive.) */
const GK_GLOW: [number, number] = [0x00b5c6, 0xc21e88]; // D69: cyan-teal glow (blue) / magenta glow (red)
const BALL_R = 0.17; // D34: ×1.5 visual ball (SIM radius stays 0.11 — presentation only)
const GK_HOLD_BALL_H = 1.0; // D34: a holding keeper shows the ball at chest height ("hands = safe")
const BALL_ROLL_AXIS = new THREE.Vector3(); // D53: scratch axis for the per-frame roll (no per-frame alloc)

/** D53: classic black-pentagon football texture (procedural equirect canvas) — spin becomes visible.
 *  Twelve black patches at the icosahedron vertex directions, wrapped at the seam, latitude-stretched. */
function makeBallTexture(): THREE.CanvasTexture {
  const W = 512;
  const H = 256;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d')!;
  g.fillStyle = '#f4f4f2';
  g.fillRect(0, 0, W, H);
  g.fillStyle = '#151515';
  const phi = (1 + Math.sqrt(5)) / 2;
  const verts: Array<[number, number, number]> = [];
  for (const s1 of [-1, 1]) for (const s2 of [-1, 1]) verts.push([0, s1, s2 * phi], [s1, s2 * phi, 0], [s2 * phi, 0, s1]);
  const r0 = 26; // patch radius (px) at the equator
  for (const [vx, vy, vz] of verts) {
    const len = Math.hypot(vx, vy, vz);
    const u = 0.5 + Math.atan2(vz / len, vx / len) / (2 * Math.PI);
    const v = 0.5 - Math.asin(vy / len) / Math.PI;
    const stretch = Math.min(3, 1 / Math.max(0.2, Math.cos((v - 0.5) * Math.PI))); // equirect widens near the poles
    for (const wrap of [-1, 0, 1]) {
      const cx = (u + wrap) * W;
      if (cx < -r0 * stretch || cx > W + r0 * stretch) continue;
      g.beginPath();
      g.ellipse(cx, v * H, r0 * stretch, r0, 0, 0, Math.PI * 2);
      g.fill();
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
const SWITCH_EASE_MS = 750; // §13.2/D36 — slower glide on switch so the eye can follow (matches switching.HANDOVER_TICKS: 750 ms ease + 0.5 s bot-yield)

export class FafiScene {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly players = new Map<number, PlayerVisual>();
  private readonly ball: THREE.Mesh;
  private refVisual: PlayerVisual;
  /** Set when the GLB pool finishes loading; entities swap pills → rigs from the next frame. */
  private pool: AnimatedPlayerPool | null = null;
  /** D37: back-number provider — replace when M7 profiles/jerseys carry real squad numbers. Evaluated
   *  once per entity at rig spawn; return null for no number. */
  backNumberFor: (e: { id: number; team: number; isGk: boolean }) => number | null = defaultBackNumber;
  private lastUpdateMs = -1;
  private lastRefPos: { x: number; y: number } | null = null;
  /** D56: the WebGL canvas — the clip recorder streams it. */
  get canvas(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  /** World angle the follow-cam faces (0 = looking +worldX). Set by the mouse camera control. */
  cameraYaw = 0;
  // D108 goal-celebration pan. The client owns the clock: ViewState carries the phase but not how far
  // through it we are, and a presentation-only sweep does not justify widening the snapshot.
  private celebrationStartMs: number | null = null;
  private celebrationBaseYaw = 0;
  /** look-down orbit angle (rad) — set from CameraControl.pitch (D18 vertical look). */
  cameraPitch = (30 * Math.PI) / 180;
  private readonly raycaster = new THREE.Raycaster();
  // follow-cam smoothing (§13.2): tightly tracks the controlled player, but eases over SWITCH_EASE_MS when
  // control switches to a new entity so the view glides rather than snapping.
  private camAnchor: { x: number; z: number } | null = null;
  private lastControlledId = -1;
  private easeStartMs = -Infinity;
  private easeFrom: { x: number; z: number } = { x: 0, z: 0 };

  constructor(
    private readonly container: HTMLElement,
    private readonly cfg: SceneConfig,
  ) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, cfg.maxPixelRatio ?? 2));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(55, container.clientWidth / container.clientHeight, 0.1, 500);
    this.camera.position.set(-9, 7, 0);
    this.camera.lookAt(0, 0.5, 0);

    // D34: bright environment (sky/lights/striped pitch/outer ground/boards/stands+crowd)
    buildEnvironment(this.scene, { pitchLength: this.cfg.pitchLength, pitchWidth: this.cfg.pitchWidth });

    this.buildPitch();

    this.ball = new THREE.Mesh(
      new THREE.SphereGeometry(BALL_R, 24, 18),
      new THREE.MeshStandardMaterial({ map: makeBallTexture(), roughness: 0.6 }), // D53: classic black/white panels — rotation reads
    );
    this.scene.add(this.ball);
    this.refVisual = new CapsuleVisual(this.makeCapsule(0x111111));
    this.scene.add(this.refVisual.object3d);
    this.ripple = this.makeRipple();
    // D34: pass-target ring — marks the teammate a PASS would soft-lock (team-colored, gentle pulse)
    this.passRing = new THREE.Mesh(
      new THREE.RingGeometry(0.55, 0.7, 32),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false }),
    );
    this.passRing.rotation.x = -Math.PI / 2;
    this.passRing.visible = false;
    this.scene.add(this.passRing);

    // D21: pills are instant; the skinned players swap in when (if) the GLB pool is ready
    void AnimatedPlayerPool.load()
      .then((pool) => {
        this.pool = pool;
      })
      .catch((err: unknown) => {
        console.warn('animated player models unavailable — keeping pill renderer', err);
      });

    // D18: ball silhouette drawn ONLY where occluded (inverted depth test) — never lose the ball behind a pill
    this.ballXray = new THREE.Mesh(
      new THREE.SphereGeometry(BALL_R * 1.15, 16, 12),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.45, depthTest: true, depthWrite: false }),
    );
    (this.ballXray.material as THREE.MeshBasicMaterial).depthFunc = THREE.GreaterDepth;
    this.ballXray.renderOrder = 999;
    this.scene.add(this.ballXray);
    // D18: soft ground shadow under the ball — depth cue for lofted balls
    this.ballShadow = new THREE.Mesh(
      new THREE.CircleGeometry(BALL_R * 1.6, 20),
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.35, depthWrite: false }),
    );
    this.ballShadow.rotation.x = -Math.PI / 2;
    this.scene.add(this.ballShadow);
  }
  private readonly ballXray!: THREE.Mesh;
  private readonly ballShadow!: THREE.Mesh;
  private ballPrevX = 0; // D53: last rendered ball position — drives the roll
  private ballPrevZ = 0;

  private buildPitch(): void {
    const { pitchLength: L, pitchWidth: W } = this.cfg;
    // the grass plane itself is built by buildEnvironment (striped); here: lines, circle, goals, boxes
    const lineMat = new THREE.LineBasicMaterial({ color: 0xffffff });
    // halfway line (along worldY at x=0 ⇒ three z-axis)
    this.scene.add(
      new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0.02, -W / 2), new THREE.Vector3(0, 0.02, W / 2)]),
        lineMat,
      ),
    );
    const circle: THREE.Vector3[] = [];
    for (let i = 0; i <= 48; i++) {
      const a = (i / 48) * Math.PI * 2;
      circle.push(new THREE.Vector3(Math.cos(a) * 3, 0.02, Math.sin(a) * 3));
    }
    this.scene.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(circle), lineMat));
    // goal lines
    for (const sx of [-L / 2, L / 2]) {
      this.scene.add(
        new THREE.Line(
          new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(sx, 0.02, -W / 2), new THREE.Vector3(sx, 0.02, W / 2)]),
          lineMat,
        ),
      );
      this.buildGoal(sx);
      this.buildBoxes(sx);
    }
  }

  /** D18: penalty box + goal area lines at the goal-line x = `sx` (the keeper's handling zone). */
  private buildBoxes(sx: number): void {
    const lineMat = new THREE.LineBasicMaterial({ color: 0xffffff });
    const inward = -Math.sign(sx); // toward midfield
    const rect = (depth: number, halfW: number): THREE.Vector3[] => [
      new THREE.Vector3(sx, 0.02, -halfW),
      new THREE.Vector3(sx + inward * depth, 0.02, -halfW),
      new THREE.Vector3(sx + inward * depth, 0.02, halfW),
      new THREE.Vector3(sx, 0.02, halfW),
    ];
    for (const [d, w] of [
      [this.cfg.penaltyBoxDepth, this.cfg.penaltyBoxHalfWidth],
      [this.cfg.goalAreaDepth, this.cfg.goalWidth / 2 + this.cfg.goalAreaDepth],
    ] as const) {
      this.scene.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(rect(d, w)), lineMat));
    }
  }

  /** D65d: the two goal frames — each takes the DEFENDING team's colour, recoloured on the half-time flip. */
  private readonly goalFrames: Array<{ sx: number; frame: THREE.MeshStandardMaterial; net: THREE.MeshBasicMaterial }> = [];
  private goalAttackDir0 = 0; // last-applied attackDir[0] (0 = never applied ⇒ recolour on first update)

  /** A goal frame at sim x=`sx`: two posts + a crossbar + a faint net plane, coloured by the DEFENDER (D65d). */
  private buildGoal(sx: number): void {
    const gw = this.cfg.goalWidth;
    const h = this.cfg.barH;
    const frameMat = new THREE.MeshStandardMaterial({ color: 0xffffff });
    const post = (z: number): THREE.Mesh => {
      const m = new THREE.Mesh(new THREE.CylinderGeometry(POST_R, POST_R, h, 10), frameMat);
      m.position.set(sx, h / 2, z); // three: (sim.x, height, sim.y)
      return m;
    };
    this.scene.add(post(-gw / 2));
    this.scene.add(post(gw / 2));
    const bar = new THREE.Mesh(new THREE.CylinderGeometry(POST_R, POST_R, gw + POST_R * 2, 10), frameMat);
    bar.rotation.x = Math.PI / 2; // cylinder axis (+y) → along z (the goal mouth width)
    bar.position.set(sx, h, 0);
    this.scene.add(bar);
    // net hint: a translucent plane just behind the line
    const depth = Math.min(2, gw * 0.6);
    const netMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.12, side: THREE.DoubleSide });
    const net = new THREE.Mesh(new THREE.PlaneGeometry(gw, h), netMat);
    net.rotation.y = Math.PI / 2; // face along ±x
    net.position.set(sx + Math.sign(sx) * depth, h / 2, 0);
    this.scene.add(net);
    this.goalFrames.push({ sx, frame: frameMat, net: netMat });
  }

  /** D65d: recolour each goal to the team DEFENDING it. The goal at x=sx is a team's OWN goal iff that
   *  team's attackDir points AWAY from sx (own goal sits behind them). Flips at half-time with attackDir0. */
  private recolorGoals(attackDir0: 1 | -1): void {
    if (attackDir0 === this.goalAttackDir0) return; // only on a flip
    this.goalAttackDir0 = attackDir0;
    for (const g of this.goalFrames) {
      // team 0 attacks +attackDir0; its own goal is at −attackDir0·half. So the goal at sign(sx) is
      // defended by team 0 iff sign(sx) === −attackDir0, else team 1.
      const defender = Math.sign(g.sx) === -attackDir0 ? 0 : 1;
      const hex = this.cfg.teamColors[defender];
      g.frame.color.setHex(hex);
      g.net.color.setHex(hex);
    }
  }

  private makeCapsule(color: number, heightScale = 1): THREE.Mesh {
    // D18: slim visual pill (radius PILL_R ∝ ball); total height ≈ 1.8 m × per-player variation
    const m = new THREE.Mesh(new THREE.CapsuleGeometry(PILL_R, 1.36 * heightScale, 6, 12), new THREE.MeshStandardMaterial({ color }));
    m.userData['baseColor'] = color; // telegraph tinting restores from this (D17)
    return m;
  }

  /** Deterministic ±4% height variation from the entity id — identical on every client (D18). */
  private static heightVar(id: number): number {
    const h = ((id * 2654435761) >>> 0) % 1000; // Knuth hash — stable, no Math.random
    return 0.96 + 0.08 * (h / 999);
  }

  // ── contact telegraphs (D17; extended D46 Item 8): learnable timing needs visible state ──
  // active tackle/shove/trap → emissive flash + a rising-edge ground ripple; recovery/prone/getup → dimmed.
  // D46 Item 8: SHOVE (warm) + TRAP (teal) reuse the tackle telegraph, differentiated by ripple tint +
  // radius (trap ≈ its 1.0 m reach, shove ≈ the barge reach). TRAP/SHOVE keep locomotion body-wise (no
  // bespoke clip) — the ground ripple IS the feedback, per the designer's confirmed intent.
  private static readonly TELEGRAPH_FLASH: Record<string, number> = {
    TACKLE_STAND: 0x1f9e4a,
    TACKLE_SLIDE: 0x1f9e4a,
    SHOVE: 0xd98a2b, // warm
    TRAP: 0x1fb0c6, // teal
    BLOCK: 0x2b6fd9, // D82: steel-blue braced wall — a held stance glow, "he's bracing"
    SHIELD: 0xd9b84f, // D82: gold — the carrier is shielding the ball (X now visibly does something)
  };
  private static readonly TELEGRAPH_TINT: Record<string, number> = {
    TACKLE_STAND: 0xffffff,
    TACKLE_SLIDE: 0xffffff,
    SHOVE: 0xffcf9a, // warm ring
    TRAP: 0x9af0ff, // teal ring
    BLOCK: 0x9ab8ff, // steel ring ≈ block body
    SHIELD: 0xffe89a, // gold ring ≈ the shielded ball
  };
  private static readonly TELEGRAPH_MAXSCALE: Record<string, number> = {
    TACKLE_STAND: 1.35,
    TACKLE_SLIDE: 1.35,
    SHOVE: 1.2, // ≈ barge reach
    TRAP: 1.0, // ≈ trap reach
    BLOCK: 1.3, // ≈ the wider braced body
    SHIELD: 0.9, // ≈ tight to the shielded ball
  };
  private static readonly RECOVERY_ACTIONS = new Set(['SLIDE_RECOVERY', 'PRONE', 'GETUP']);
  /** D108: cfg.rules.GOAL_CELEBRATION_TICKS (150) at 30 Hz. Presentation clock, hence ms. */
  private static readonly CELEBRATION_MS = (150 / 30) * 1000;
  private readonly ripple: THREE.Mesh;
  private rippleMaxScale = 1.35; // D46: target ripple radius for the current telegraph (per verb)
  // D39: possession-gained pulse — a quick expanding ring under YOUR player the instant a claim lands,
  // so "did I get it?" answers itself.
  private possessPulse: THREE.Mesh | null = null;
  // D45: charged-kick trajectory preview — two-tone billboarded 3D dots + landing ring (fields below near buildTraj)
  private trajLanding: THREE.Mesh | null = null;
  // D43: floating name tags over OTHER humans' players (net play)
  private tagDivs = new Map<number, HTMLDivElement>();
  private tagNames = new Map<number, string>();
  private possessBornMs = -1;
  private prevControlledHasBall = false;
  private prevControlledMoveSpeed = 0;
  private touchTick: THREE.Mesh | null = null;
  private touchBornMs = -1;
  /** D44 Item 1: soft audio cue for a sprint knock-on touch (injected by the composition root — the
   *  render layer never touches audio globals; §0.2 boundary). */
  private touchCue: (() => void) | null = null;
  /** D82: audio cue for the CONTROLLED player's own whiffed tackle (injected — render never touches audio). */
  private whiffCue: (() => void) | null = null;
  private readonly passRing!: THREE.Mesh;
  private rippleBornMs = -1;

  /** Wire the sprint-touch sound (a soft thump) — main.ts owns the Sfx instance. */
  setWhiffCue(cb: () => void): void {
    this.whiffCue = cb;
  }

  setTouchCue(cb: () => void): void {
    this.touchCue = cb;
  }

  private makeRipple(): THREE.Mesh {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.86, 1, 32),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.visible = false;
    this.scene.add(ring);
    return ring;
  }

  /** Spawn the reach ripple at an entity that just started a tackle; animate/fade in update(). */
  private telegraph(e: ViewEntity, v: PlayerVisual, nowMs: number): void {
    const base = v.userData['baseColor'] as number;
    const flash = FafiScene.TELEGRAPH_FLASH[e.action]; // tackle/shove/trap → a contact telegraph
    if (flash !== undefined) {
      v.setEmissive(flash);
      if (v.userData['prevAction'] !== e.action) {
        // rising edge: ripple ring at the actor, tinted + sized by the verb (D46 Item 8)
        this.ripple.position.set(e.pos.x, 0.03, e.pos.y);
        this.rippleBornMs = nowMs;
        this.rippleMaxScale = FafiScene.TELEGRAPH_MAXSCALE[e.action] ?? 1.35;
        (this.ripple.material as THREE.MeshBasicMaterial).color.setHex(FafiScene.TELEGRAPH_TINT[e.action] ?? 0xffffff);
        this.ripple.visible = true;
      }
    } else {
      // controlled marker wins; otherwise the keeper keeps his permanent neon glow (D38)
      v.setEmissive(e.isControlled ? 0x554400 : e.isGk ? GK_GLOW[e.team] : 0x000000);
    }
    v.setColor(base);
    if (FafiScene.RECOVERY_ACTIONS.has(e.action)) v.mulColor(0.35); // locked out — dim
    // D82 whiff feedback: the CONTROLLED player's stand tackle that WON nothing stumbles to GETUP — a
    // whoosh through empty air (a missed slide keeps its own SLIDE_RECOVERY, unambiguous only for stand).
    const prev = v.userData['prevAction'];
    if (e.isControlled && e.action === 'GETUP' && prev === 'TACKLE_STAND') this.whiffCue?.();
    v.userData['prevAction'] = e.action;
  }

  private animateRipple(nowMs: number): void {
    if (this.rippleBornMs < 0) return;
    const t = (nowMs - this.rippleBornMs) / 250; // 250 ms life
    if (t >= 1) {
      this.ripple.visible = false;
      this.rippleBornMs = -1;
      return;
    }
    const s = 0.5 + t * (this.rippleMaxScale - 0.5); // expands toward the verb's reach (presentation-only)
    this.ripple.scale.set(s, s, s);
    (this.ripple.material as THREE.MeshBasicMaterial).opacity = 0.55 * (1 - t);
  }

  /** D132: the back-cone flick — a short amber arc on the SHIELDED carrier's back when your tackle or
   *  trap is refused by the D80 cone. Presentation only (invariant 8): no event, no state, and it reads
   *  the same geometry the sim rejects on. Re-arming while it is already lit is a no-op, so a held tackle
   *  pulses once rather than strobing. */
  showConeReject(x: number, y: number, facing: number, nowMs: number): void {
    if (!this.coneFlick) {
      // a 100° arc centred on the carrier's BACK — the side you must come around
      const arc = new THREE.RingGeometry(0.62, 0.86, 24, 1, Math.PI - 0.87, 1.74);
      this.coneFlick = new THREE.Mesh(arc, new THREE.MeshBasicMaterial({ color: 0xffb03a, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false }));
      this.coneFlick.rotation.x = -Math.PI / 2;
      this.scene.add(this.coneFlick);
    }
    if (this.coneBornMs >= 0 && nowMs - this.coneBornMs < 260) return; // already lit — do not strobe
    this.coneFlick.position.set(x, 0.05, y);
    this.coneFlick.rotation.z = -facing;
    this.coneFlick.visible = true;
    this.coneBornMs = nowMs;
  }

  private animateConeFlick(nowMs: number): void {
    if (!this.coneFlick || this.coneBornMs < 0) return;
    const t = (nowMs - this.coneBornMs) / 320;
    if (t >= 1) {
      this.coneFlick.visible = false;
      this.coneBornMs = -1;
      return;
    }
    (this.coneFlick.material as THREE.MeshBasicMaterial).opacity = 0.85 * (1 - t);
  }

  /** D39: green claim-confirm ring; expands + fades over ~350 ms. */
  private spawnPossessPulse(x: number, y: number, nowMs: number): void {
    if (!this.possessPulse) {
      this.possessPulse = new THREE.Mesh(
        new THREE.RingGeometry(0.5, 0.66, 32),
        new THREE.MeshBasicMaterial({ color: 0x5cff9d, transparent: true, opacity: 0.9, side: THREE.DoubleSide, depthWrite: false }),
      );
      this.possessPulse.rotation.x = -Math.PI / 2;
      this.scene.add(this.possessPulse);
    }
    this.possessPulse.position.set(x, 0.04, y);
    this.possessPulse.visible = true;
    this.possessBornMs = nowMs;
  }

  private coneFlick: THREE.Mesh | null = null;
  private coneBornMs = -1;

  private animatePossessPulse(nowMs: number): void {
    if (!this.possessPulse || this.possessBornMs < 0) return;
    const t = (nowMs - this.possessBornMs) / 350;
    if (t >= 1) {
      this.possessPulse.visible = false;
      this.possessBornMs = -1;
      return;
    }
    const sc = 1 + t * 1.1;
    this.possessPulse.scale.set(sc, sc, sc);
    (this.possessPulse.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - t);
  }

  // D44 Item 1: a small DIM white "touch" tick at a sprint knock-on — reads the remaining (sprint-only)
  // knock as an intentional long touch, not a mystery loss. Fainter + quicker than the claim pulse.
  private static readonly SPRINT_TOUCH_SPEED = 8.3; // > base MAX_SPEED (8.0): a held sprint, not a run
  private spawnTouchTick(x: number, y: number, nowMs: number): void {
    if (!this.touchTick) {
      this.touchTick = new THREE.Mesh(
        new THREE.RingGeometry(0.28, 0.4, 24),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.4, side: THREE.DoubleSide, depthWrite: false }),
      );
      this.touchTick.rotation.x = -Math.PI / 2;
      this.scene.add(this.touchTick);
    }
    this.touchTick.position.set(x, 0.04, y);
    this.touchTick.visible = true;
    this.touchBornMs = nowMs;
    this.touchCue?.();
  }

  private animateTouchTick(nowMs: number): void {
    if (!this.touchTick || this.touchBornMs < 0) return;
    const t = (nowMs - this.touchBornMs) / 250;
    if (t >= 1) {
      this.touchTick.visible = false;
      this.touchBornMs = -1;
      return;
    }
    const sc = 1 + t * 1.4;
    this.touchTick.scale.set(sc, sc, sc);
    (this.touchTick.material as THREE.MeshBasicMaterial).opacity = 0.4 * (1 - t);
  }

  /** D44 (Item 11): draw the charged-kick trajectory as instanced dot markers — a golf-game arc, readable
   *  at any distance (the old 1-px dashed line was invisible over the bright pitch). ~24 flat discs fading
   *  0.9 → 0.2 along the path + a larger landing ring at the end. Updated every frame while charging. */
  // D45 Item 4: the dots fly the TRUE 3D arc (loft + bounce). Two-tone instancing for contrast on any
  // background — a dark backing disc under a white core, both billboarded to the camera each frame.
  private static readonly TRAJ_DOTS = 48;
  private trajCore: THREE.InstancedMesh | null = null; // white centres
  private trajBack: THREE.InstancedMesh | null = null; // dark halos (drawn first)
  private trajLandBack: THREE.Mesh | null = null;
  private buildTraj(): void {
    const disc = new THREE.CircleGeometry(1, 14); // unit disc, scaled per-instance for the size taper
    const back = new THREE.MeshBasicMaterial({ color: 0x10241a, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false });
    const core = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.95, side: THREE.DoubleSide, depthWrite: false });
    this.trajBack = new THREE.InstancedMesh(disc, back, FafiScene.TRAJ_DOTS);
    this.trajCore = new THREE.InstancedMesh(disc, core, FafiScene.TRAJ_DOTS);
    for (const mesh of [this.trajBack, this.trajCore]) {
      mesh.frustumCulled = false;
      mesh.renderOrder = 50;
      this.scene.add(mesh);
    }
    this.trajCore.renderOrder = 51; // white core over its dark halo
    const ring = new THREE.RingGeometry(0.16, 0.26, 24);
    ring.rotateX(-Math.PI / 2);
    const ringBack = new THREE.RingGeometry(0.14, 0.30, 24);
    ringBack.rotateX(-Math.PI / 2);
    this.trajLandBack = new THREE.Mesh(ringBack, new THREE.MeshBasicMaterial({ color: 0x10241a, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false }));
    this.trajLanding = new THREE.Mesh(ring, new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, side: THREE.DoubleSide, depthWrite: false }));
    for (const mesh of [this.trajLandBack, this.trajLanding]) {
      mesh.frustumCulled = false;
      mesh.renderOrder = 50;
      this.scene.add(mesh);
    }
    this.trajLanding.renderOrder = 51;
  }

  showTrajectory(points: Array<{ x: number; y: number; z: number }>): void {
    if (!this.trajCore || !this.trajBack || !this.trajLanding || !this.trajLandBack) this.buildTraj();
    const core = this.trajCore!;
    const back = this.trajBack!;
    const chosen = selectTrajectoryDots(points, FafiScene.TRAJ_DOTS);
    const m = new THREE.Matrix4();
    const pos = new THREE.Vector3();
    const scl = new THREE.Vector3();
    const q = this.camera.quaternion; // billboard: every disc faces the camera
    const hidden = new THREE.Matrix4().makeScale(0, 0, 0);
    for (let i = 0; i < FafiScene.TRAJ_DOTS; i++) {
      const p = chosen[i];
      if (p) {
        pos.set(p.x, p.z + BALL_R + 0.02, p.y); // TRUE 3D arc (sim z-up → three y-up), just off the ball
        const r = 0.11 - 0.04 * (chosen.length > 1 ? i / (chosen.length - 1) : 0); // taper 0.11 → 0.07
        scl.set(r, r, r);
        core.setMatrixAt(i, m.compose(pos, q, scl));
        scl.set(r + 0.05, r + 0.05, r + 0.05);
        back.setMatrixAt(i, m.compose(pos, q, scl));
      } else {
        core.setMatrixAt(i, hidden);
        back.setMatrixAt(i, hidden);
      }
    }
    core.instanceMatrix.needsUpdate = true;
    back.instanceMatrix.needsUpdate = true;
    core.visible = true;
    back.visible = true;
    const land = chosen[chosen.length - 1] ?? points[points.length - 1];
    if (land) {
      const y = land.z + BALL_R + 0.02;
      this.trajLanding!.position.set(land.x, y, land.y);
      this.trajLandBack!.position.set(land.x, y - 0.001, land.y);
      this.trajLanding!.visible = true;
      this.trajLandBack!.visible = true;
    }
  }

  hideTrajectory(): void {
    for (const mesh of [this.trajCore, this.trajBack, this.trajLanding, this.trajLandBack]) {
      if (mesh) mesh.visible = false;
    }
  }

  // D110 (HB-A §1.4): the LANDING RING — where a hanging ball will touch down. Both teams see it; reading
  // flight is how football is played. Flat on the pitch, tightening as the ball drops onto it.
  private landingRing: THREE.Mesh | null = null;
  showLanding(x: number, y: number, progress01: number): void {
    if (!this.landingRing) {
      const ring = new THREE.RingGeometry(0.55, 0.75, 32);
      ring.rotateX(-Math.PI / 2); // lie flat on the turf
      const mat = new THREE.MeshBasicMaterial({ color: 0xffee88, transparent: true, opacity: 0.55, side: THREE.DoubleSide, depthWrite: false });
      this.landingRing = new THREE.Mesh(ring, mat);
      this.landingRing.frustumCulled = false;
      this.landingRing.renderOrder = 49; // under the charge preview, over the turf
      this.scene.add(this.landingRing);
    }
    const s = 1.35 - 0.55 * Math.max(0, Math.min(1, progress01)); // wide while it hangs, tight on arrival
    this.landingRing.position.set(x, 0.02, y);
    this.landingRing.scale.set(s, 1, s);
    (this.landingRing.material as THREE.MeshBasicMaterial).opacity = 0.35 + 0.4 * Math.max(0, Math.min(1, progress01));
    this.landingRing.visible = true;
  }
  hideLanding(): void {
    if (this.landingRing) this.landingRing.visible = false;
  }

  // D45 Item 3b: the goal precision-aim reticle — a ring ON the goal plane at goalAim, standing up (facing
  // the pitch), depthTest:false so the keeper never hides it. It IS the aim: what you see is where it arrives.
  private goalReticle: THREE.Mesh | null = null;
  showGoalReticle(x: number, y: number, z: number, attackSign: number): void {
    if (!this.goalReticle) {
      const ring = new THREE.RingGeometry(0.22, 0.34, 28);
      const mat = new THREE.MeshBasicMaterial({ color: 0xffee55, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthTest: false, depthWrite: false });
      this.goalReticle = new THREE.Mesh(ring, mat);
      this.goalReticle.frustumCulled = false;
      this.goalReticle.renderOrder = 60;
      this.scene.add(this.goalReticle);
    }
    this.goalReticle.position.set(x, z, y); // sim (x,y,z-up) → three (x, y-up, z)
    this.goalReticle.rotation.set(0, attackSign > 0 ? Math.PI / 2 : -Math.PI / 2, 0); // stand up, face the pitch
    this.goalReticle.visible = true;
  }
  hideGoalReticle(): void {
    if (this.goalReticle) this.goalReticle.visible = false;
  }

  /**
   * D146 (playtest 5, report 3) — the lesson's render-only markers.
   *
   * `LessonSpec.props` shipped in D139 with data for three gates in `first-touch`, whose copy says
   * "Dribble through the two cones" — and had **no consumer anywhere in the client**. The player was
   * told to dribble through cones on a completely empty pitch. Objectives read POSITIONS, never these,
   * so this is presentation only: a gate is the pair of cones the detector's radius already implies.
   *
   * Built once per lesson (props are static), so there is nothing per-frame here.
   */
  private lessonProps: THREE.Group | null = null;
  setLessonProps(props: ReadonlyArray<{ kind: 'gate' | 'cone' | 'zone'; x: number; y: number; r: number }>): void {
    if (this.lessonProps) {
      this.scene.remove(this.lessonProps);
      this.lessonProps = null;
    }
    if (props.length === 0) return;
    const group = new THREE.Group();
    const coneGeo = new THREE.ConeGeometry(0.17, 0.42, 12);
    const coneMat = new THREE.MeshLambertMaterial({ color: 0xff9a3c });
    const cone = (x: number, y: number): void => {
      const m = new THREE.Mesh(coneGeo, coneMat);
      m.position.set(x, 0.21, y); // sim (x,y) → three (x, y-up, z)
      m.castShadow = true;
      group.add(m);
    };
    for (const p of props) {
      if (p.kind === 'cone') {
        cone(p.x, p.y);
      } else if (p.kind === 'gate') {
        // a gate is two cones `r` apart, square to the goal — you dribble BETWEEN them
        cone(p.x, p.y - p.r);
        cone(p.x, p.y + p.r);
      } else {
        const ring = new THREE.RingGeometry(Math.max(0.1, p.r - 0.12), p.r, 40);
        ring.rotateX(-Math.PI / 2);
        const mesh = new THREE.Mesh(ring, new THREE.MeshBasicMaterial({ color: 0xffd166, transparent: true, opacity: 0.5, side: THREE.DoubleSide, depthWrite: false }));
        mesh.position.set(p.x, 0.02, p.y);
        mesh.renderOrder = 48;
        group.add(mesh);
      }
    }
    this.lessonProps = group;
    this.scene.add(group);
  }

  /** D43: name tags for OTHER humans — set every frame (cheap); positions update in updateNameTags(). */
  setNameTags(tags: Array<{ entityId: number; name: string }>): void {
    const want = new Set(tags.map((t) => t.entityId));
    for (const [id, div] of this.tagDivs) {
      if (!want.has(id)) {
        div.remove();
        this.tagDivs.delete(id);
        this.tagNames.delete(id);
      }
    }
    for (const t of tags) {
      let div = this.tagDivs.get(t.entityId);
      if (!div) {
        div = document.createElement('div');
        div.style.cssText =
          'position:fixed;transform:translate(-50%,-100%);padding:1px 8px;border-radius:6px;background:rgba(8,18,12,.8);' +
          'color:#eaffea;font:700 11px ui-monospace,monospace;pointer-events:none;z-index:52;white-space:nowrap;letter-spacing:.5px';
        document.body.appendChild(div);
        this.tagDivs.set(t.entityId, div);
      }
      if (this.tagNames.get(t.entityId) !== t.name) {
        div.textContent = t.name;
        this.tagNames.set(t.entityId, t.name);
      }
    }
  }

  private updateNameTags(view: ViewState): void {
    if (this.tagDivs.size === 0) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    const v = new THREE.Vector3();
    for (const [id, div] of this.tagDivs) {
      const e = view.entities.find((x) => x.id === id);
      if (!e) {
        div.style.display = 'none';
        continue;
      }
      v.set(e.pos.x, 2.25, e.pos.y).project(this.camera);
      if (v.z > 1 || v.z < -1) {
        div.style.display = 'none'; // behind the camera
        continue;
      }
      div.style.display = 'block';
      div.style.left = `${rect.left + ((v.x + 1) / 2) * rect.width}px`;
      div.style.top = `${rect.top + ((1 - v.y) / 2) * rect.height}px`;
    }
  }

  /** D68: an outfielder's kit — the `teamKits` override if set, else a full unicolor jersey in the team
   *  colour (faintly shaded, no stripes). */
  private outfieldKit(team: ViewEntity['team']): KitPaint {
    return this.cfg.teamKits?.[team] ?? unicolorKit(this.cfg.teamColors[team]);
  }

  private entityVisual(e: ViewEntity): PlayerVisual {
    const color = e.isGk ? GK_NEON[e.team] : this.cfg.teamColors[e.team]; // D69: cyan/magenta keepers
    let v = this.players.get(e.id);
    if (!v) {
      const mesh = this.makeCapsule(color, FafiScene.heightVar(e.id));
      v = new CapsuleVisual(mesh);
      if (e.isGk) v.setEmissive(GK_GLOW[e.team]);
      this.players.set(e.id, v);
      this.scene.add(mesh);
    }
    // D21: swap the pill for a skinned rig once the pool is loaded (mid-match join is fine — the
    // rig picks up the entity's current action on its first tick)
    if (this.pool && v instanceof CapsuleVisual) {
      // D69: each entity's KitPaint repaints the two baked inks (see kitRecolor). Keepers take a solid
      // neon kit (cyan/magenta per team); outfielders take a solid unicolor jersey in their team colour —
      // or the per-team `teamKits` override for full control. The referee kit is built in updateRef().
      const kit = e.isGk ? gkKit(e.team) : this.outfieldKit(e.team);
      // D109: the entity id seeds this rig's idle phase + detune, so no two players breathe in step.
      const rig = this.pool.spawnRig(kit, FafiScene.heightVar(e.id), this.backNumberFor(e), e.id);
      const rv = new RigVisual(rig, 0xffffff); // white base — kit colour rides the shader (D34)
      if (e.isGk) rv.setEmissive(GK_GLOW[e.team]);
      this.scene.remove(v.object3d);
      this.scene.add(rv.object3d);
      this.players.set(e.id, rv);
      v = rv;
    }
    return v;
  }

  /** Raycast the pointer (NDC) onto the pitch plane; returns the sim-space (x, y) hit, or null if it misses. */
  pitchHitFromNdc(ndc: { x: number; y: number }): { x: number; y: number } | null {
    this.raycaster.setFromCamera(new THREE.Vector2(ndc.x, ndc.y), this.camera);
    const { origin, direction } = this.raycaster.ray;
    if (Math.abs(direction.y) < 1e-9) return null; // parallel to the pitch
    const t = -origin.y / direction.y;
    if (t < 0) return null; // pitch is behind the camera along the ray
    return { x: origin.x + direction.x * t, y: origin.z + direction.z * t }; // three (x,z) → sim (x,y)
  }

  update(view: ViewState): void {
    const nowMs = performance.now(); // render-side wall clock — presentation only (§0.2 boundary intact)
    const dtSec = this.lastUpdateMs < 0 ? 0 : Math.min((nowMs - this.lastUpdateMs) / 1000, 0.1);
    this.lastUpdateMs = nowMs;
    this.recolorGoals(view.attackDir0); // D65d: team-coloured goals (defender's colour; flips at half-time)
    let controlled: ViewEntity | undefined;
    for (const e of view.entities) {
      const v = this.entityVisual(e);
      v.object3d.position.set(e.pos.x, e.pos.z + v.groundOffset, e.pos.y);
      v.object3d.rotation.y = -e.facing + Math.PI / 2;
      this.telegraph(e, v, nowMs); // controlled highlight + tackle flash + recovery dim (D17)
      if (e.isControlled) {
        if (e.hasBall && !this.prevControlledHasBall) this.spawnPossessPulse(e.pos.x, e.pos.y, nowMs); // D39
        // D44: lost the ball while sprinting ⇒ a dim touch tick at the ball (a knock-on, not a mystery loss)
        if (!e.hasBall && this.prevControlledHasBall && this.prevControlledMoveSpeed > FafiScene.SPRINT_TOUCH_SPEED) {
          this.spawnTouchTick(view.ball.pos.x, view.ball.pos.y, nowMs);
        }
        this.prevControlledHasBall = e.hasBall;
        this.prevControlledMoveSpeed = e.moveSpeed;
      }
      // D108: the running score rerolls the celebration clip per goal — it changes on every goal and
      // is identical on every client, so nobody needs the GOAL event or a protocol field for it.
      v.tick(e, dtSec, view.hud.score[0] + view.hud.score[1]);
      if (e.isControlled) controlled = e;
    }
    this.animateRipple(nowMs);
    this.animatePossessPulse(nowMs);
    this.animateConeFlick(nowMs); // D132
    this.animateTouchTick(nowMs);
    this.updateNameTags(view);
    // D34: a holding keeper shows the ball at chest height (hands = safe); otherwise it rides the sim pos
    const holder = view.entities.find((e) => e.hasBall && e.action === 'GK_HOLD');
    const ballY = holder ? GK_HOLD_BALL_H : view.ball.pos.z + BALL_R;
    const bx = holder ? holder.pos.x + Math.cos(holder.facing) * 0.45 : view.ball.pos.x;
    const by = holder ? holder.pos.y + Math.sin(holder.facing) * 0.45 : view.ball.pos.y;
    // D53: roll the ball from its rendered DISPLACEMENT — ground contact rolls it exactly (ω = |Δ|/R
    // about up×Δ), airborne it keeps turning with its flight, carried it rolls along with the carrier.
    // One rule for every timeline (sim, interp, predicted); a teleport (restart) skips the spin.
    {
      const dx = bx - this.ballPrevX;
      const dz = by - this.ballPrevZ;
      const d = Math.hypot(dx, dz);
      if (d > 1e-5 && d < 2) {
        BALL_ROLL_AXIS.set(dz / d, 0, -dx / d);
        this.ball.rotateOnWorldAxis(BALL_ROLL_AXIS, d / BALL_R);
      }
      this.ballPrevX = bx;
      this.ballPrevZ = by;
    }
    this.ball.position.set(bx, ballY, by);
    this.ballXray.position.copy(this.ball.position); // silhouette shows only where the ball is occluded
    this.ballShadow.position.set(view.ball.pos.x, 0.015, view.ball.pos.y);
    const shScale = Math.max(0.4, 1 - view.ball.pos.z * 0.12);
    this.ballShadow.scale.set(shScale, shScale, shScale);
    (this.ballShadow.material as THREE.MeshBasicMaterial).opacity = 0.35 * Math.max(0.3, 1 - view.ball.pos.z * 0.1);
    // D34: pass-target ring under the soft-locked teammate (gentle pulse so it reads as "preview")
    const target = view.passTarget !== null ? view.entities.find((e) => e.id === view.passTarget) : undefined;
    if (target) {
      this.passRing.visible = true;
      this.passRing.position.set(target.pos.x, 0.03, target.pos.y);
      const pulse = 1 + 0.12 * Math.sin(nowMs / 180);
      this.passRing.scale.set(pulse, pulse, pulse);
      (this.passRing.material as THREE.MeshBasicMaterial).color.setHex(this.cfg.teamColors[target.team]);
    } else {
      this.passRing.visible = false;
    }
    this.updateRef(view, dtSec);

    if (controlled) {
      // a control SWITCH (the controlled entity id changed) starts a short ease from the current anchor
      if (this.lastControlledId !== -1 && controlled.id !== this.lastControlledId && this.camAnchor) {
        this.easeStartMs = nowMs;
        this.easeFrom = { ...this.camAnchor };
      }
      this.lastControlledId = controlled.id;
      const goal = { x: controlled.pos.x, z: controlled.pos.y };
      const t = (nowMs - this.easeStartMs) / SWITCH_EASE_MS;
      if (!this.camAnchor || t >= 1) {
        this.camAnchor = goal; // tight follow (no ease, or ease complete)
      } else {
        const e = 1 - (1 - t) * (1 - t); // ease-out toward the (moving) target over 0.35 s
        this.camAnchor = { x: this.easeFrom.x + (goal.x - this.easeFrom.x) * e, z: this.easeFrom.z + (goal.z - this.easeFrom.z) * e };
      }
      // orbiting follow cam: sit behind the camera-forward direction and above, look ahead of the anchor.
      // forward (worldX,worldY) = (cos yaw, sin yaw) maps to three (fx, ·, fy).
      // D108: over a goal celebration, blend from the player's follow cam toward a wide sweeping
      // pan across the celebrants and back again. `blend` is 0 at both ends, so nothing cuts.
      const celebration = this.celebrationProgress(view, nowMs);
      const blend = celebrationBlend(celebration);
      let yaw = this.cameraYaw;
      let pitch = this.cameraPitch;
      let range = 11.4; // D18 vertical look: orbit at fixed range, elevation from cameraPitch (mouse Y)
      let tx = this.camAnchor.x;
      let tz = this.camAnchor.z;
      if (blend > 0) {
        const anchor = celebrationAnchor(view.entities, { x: view.ball.pos.x, z: view.ball.pos.y });
        const f = celebrationFraming({ progress01: celebration, anchor, baseYaw: this.celebrationBaseYaw });
        const mix = (a: number, b: number): number => a + (b - a) * blend;
        yaw = mix(yaw, f.yaw);
        pitch = mix(pitch, f.pitch);
        range = mix(range, f.range);
        tx = mix(tx, f.anchor.x);
        tz = mix(tz, f.anchor.z);
      }
      const fx = Math.cos(yaw);
      const fy = Math.sin(yaw);
      const back = range * Math.cos(pitch);
      const height = Math.max(1.2, range * Math.sin(pitch));
      this.camera.position.set(tx - back * fx, height, tz - back * fy);
      // during the celebration look AT the group, not ahead of the player
      const lead = 3 * (1 - blend);
      this.camera.lookAt(tx + lead * fx, 0.6, tz + lead * fy);
    }
  }

  /**
   * D108: 0 → 1 through a goal celebration, or 0 when none is running. Latches the yaw on entry so
   * the pan starts from wherever the player was already looking, and resets on the way out so the
   * next goal sweeps afresh. GOAL_CELEBRATION_TICKS is 150 at 30 Hz = 5 s.
   */
  private celebrationProgress(view: ViewState, nowMs: number): number {
    if (view.hud.phase !== 'GOAL_CELEBRATION') {
      this.celebrationStartMs = null;
      return 0;
    }
    if (this.celebrationStartMs === null) {
      this.celebrationStartMs = nowMs;
      this.celebrationBaseYaw = this.cameraYaw;
    }
    return Math.min(1, (nowMs - this.celebrationStartMs) / FafiScene.CELEBRATION_MS);
  }

  /** D21: the ref gets the same mesh with a dark desaturated kit; only the run clip (idle = frozen). */
  private updateRef(view: ViewState, dtSec: number): void {
    if (this.pool && this.refVisual instanceof CapsuleVisual) {
      // D66: black-and-white REFEREE stripes — REF_KIT darkens the teal ink toward black while the white
      // stripes stay white; skin is untouched, so the striped mesh reads unmistakably as a referee.
      const rig = this.pool.spawnRig(REF_KIT, 1, null, 97); // arbitrary seed — just not in step with the players
      const rv = new RigVisual(rig, 0xffffff);
      this.scene.remove(this.refVisual.object3d);
      this.scene.add(rv.object3d);
      this.refVisual = rv;
    }
    const { x, y } = view.ref.pos;
    this.refVisual.object3d.position.set(x, this.refVisual.groundOffset, y);
    this.refVisual.object3d.rotation.y = -view.ref.viewDir + Math.PI / 2;
    // ViewRef carries no speed — derive it from the frame delta (presentation-only)
    let speed = 0;
    if (this.lastRefPos && dtSec > 0) speed = Math.hypot(x - this.lastRefPos.x, y - this.lastRefPos.y) / dtSec;
    this.lastRefPos = { x, y };
    if (this.refVisual instanceof RigVisual) {
      this.refVisual.rig.setClip(speed >= 0.6 ? 'run' : 'idle', speed);
      this.refVisual.rig.update(dtSec);
    }
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  resize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }
}
